import React, {useState, useEffect, useCallback} from 'react';
import {Box, Center, KeyboardAvoidingView, HStack, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';

import crashlytics from '../../utils/crashlytics/crashlytics.web';
import {StackParamList} from '../../navigators';
import ExternalTemplate from '../../components/templates/external';
import {PrimaryButton, SecondaryButton} from '../../components/buttons';
import {TextInput as Input} from '../../components/inputs/text';
import {PasswordInput as Password} from '../../components/inputs/password';
import {Link} from '../../components/links';
import {Label} from '../../components/labels';
import {useContextState as userContext} from '../../context/user';
import {ILoginForm} from '../../interfaces/ui';
import useToast from '../../hooks';
import {checkIsValidEmail} from '../../utils/validations';
import {LoginState, ResultState} from '../../constants/enumerations';
import {SpinnerModal} from '../../components/modals';
import {InfoIcon} from '../../components/icons';

type KeyChainType = {username: string; password: string};

type ScreenProps = NativeStackScreenProps<StackParamList, 'SignIn'>;

export const SignInScreen = ({navigation}: ScreenProps) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    } as ILoginForm,
  });

  const {showServerError, showInputsError, showLoginError} = useToast();
  const [keyChainSaved, setKeyChainSaved] = useState(
    false as boolean | KeyChainType,
  );
  const [loading, setLoading] = useState(false);
  const {signin, fetchEntities, checkIsOutdated, isOutdated} = userContext();
  const [autoLogin, setAutoLogin] = useState(false);

  const validateVersion = async () => {
    const result = await checkIsOutdated();
    if (result === ResultState.success) {
      return;
    }
    showServerError();
  };

  const launchOnLoad = async () => {
    await validateVersion();
  };

  useEffect(() => {
    launchOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnForget = () => {
    navigation.navigate('PasswordRecover');
  };

  const onSubmit: SubmitHandler<ILoginForm> = async (data: ILoginForm) => {
    await crashlytics().setCrashlyticsCollectionEnabled(true);
    await Promise.all([
      crashlytics().setUserId(data.email.toLowerCase()),
      crashlytics().setAttributes({
        eventDate: new Date().toString(),
      }),
    ]);

    const email = data.email.trim().toLowerCase();
    const password = data.password.trim();
    submitCredentials(email, password);
  };

  const clearSavedPassword = useCallback(() => {
    setKeyChainSaved({
      ...(keyChainSaved as KeyChainType),
      password: '',
    });
  }, [keyChainSaved]);

  const submitCredentials = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      const result = await signin(email, password);
      setLoading(false);
      if (result === LoginState.success) {
        setLoading(false);
      } else {
        setLoading(false);
        if (result === LoginState.changePassword) {
          navigation.navigate('PasswordChange');
        } else if (result === LoginState.rejected) {
          clearSavedPassword();
          showLoginError();
        } else {
          clearSavedPassword();
          showServerError();
        }
      }
    },
    [signin, navigation, clearSavedPassword, showLoginError, showServerError],
  );

  const autoSubmit = useCallback(async () => {
    const {username, password} = keyChainSaved as KeyChainType;
    if (autoLogin) {
      setAutoLogin(false);
      await submitCredentials(username, password);
    }
  }, [autoLogin, keyChainSaved, submitCredentials]);

  useEffect(() => {
    autoSubmit();
  }, [autoSubmit]);

  const onInvalid: SubmitErrorHandler<ILoginForm> = async (
    fieldErrors: FieldErrors<ILoginForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleShowTerms = async () => {
    navigation.navigate('Terms');
  };

  const fillEntities = async () => {
    setLoading(true);
    const result = await fetchEntities();
    setLoading(false);
    if (result) {
      navigation.navigate('SignUp');
    } else {
      showServerError();
    }
  };

  const handleOnRegister = async () => {
    fillEntities();
  };

  const getUserNameSaved = () => {
    const userName = keyChainSaved
      ? (keyChainSaved as KeyChainType).username
      : null;
    return userName;
  };

  const getUserPasswordSaved = () => {
    return '';
  };

  const isDisabled = () => {
    if (isOutdated) {
      return false;
    }
    return true;
  };

  return (
    <ExternalTemplate title="signin-title" summary="signin-summary">
      <KeyboardAvoidingView behavior="height" flex="1" enabled={true}>
        <SpinnerModal show={loading} />
        <Center width="100%" flex={1} justifyContent="flex-start">
          <Box width="100%">
            <Input
              control={control}
              name="email"
              type="email"
              label="signin-label-email"
              error={errors.email}
              defaultValue={getUserNameSaved()}
              rules={{
                required: 'form-login-is-required',
                validate: email =>
                  checkIsValidEmail(email) || 'form-login-is-required',
              }}
              disabled={isDisabled()}
            />
            <Password
              control={control}
              name="password"
              label="signin-label-password"
              error={errors.password}
              rules={{
                required: 'form-login-is-required',
              }}
              defaultValue={getUserPasswordSaved()}
              disabled={isDisabled()}
            />
            <HStack marginTop={5}>
              <PrimaryButton
                disabled={isDisabled()}
                label="signin-button-send"
                onPress={handleSubmit(onSubmit, onInvalid)}
              />
            </HStack>
            <Center>
              <Link
                label="signin-link-forget"
                mt="2"
                onPress={() => handleOnForget()}
                color="#0088FF"
              />
            </Center>
          </Box>
          <Box width="100%" position="absolute" bottom="0" safeArea mb="5">
            <SecondaryButton
              label="signin-button-register"
              onPress={() => handleOnRegister()}
            />
            <HStack mt="3">
              <Box justifyContent="center">
                <InfoIcon />
              </Box>
              <VStack ml={2}>
                <Label
                  caption="signin-advertice"
                  color="#858597"
                  fontWeight="medium"
                />
                <Link
                  onPress={() => handleShowTerms()}
                  label="signin-link-terms"
                  color="#0088FF"
                />
              </VStack>
            </HStack>
          </Box>
        </Center>
      </KeyboardAvoidingView>
    </ExternalTemplate>
  );
};
